import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type { CreateClient, UpdateClient } from '@trellis/shared'
import { useState } from 'react'

import { pushEvent } from '@/lib/analytics'
import {
  listClients,
  createClient,
  updateClient,
  deleteClient,
  getClient,
  getClientStats,
  assignTagsToClient,
  removeTagFromClient,
  type ClientsListParams,
} from '@/requests/clients'

/** Fetches all clients (up to 500) for tab count computation. */
export function useClientsSummaryQuery() {
  return useQuery({
    queryKey: ['clients', 'summary'],
    queryFn: () => listClients({ limit: 100 }),
    staleTime: 60_000,
  })
}

/**
 * Shared with the clients list route's loader ({@link clientsInfiniteQueryOptions}) so a
 * prefetched loader cache entry and this hook's read land on the same query key.
 */
export function clientsInfiniteQueryOptions(options: ClientsListParams = {}) {
  return infiniteQueryOptions({
    queryKey: ['clients', options],
    queryFn: ({ pageParam }) =>
      listClients({ ...options, cursor: pageParam ?? options.cursor }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.pagination?.nextCursor ?? undefined,
  })
}

export function useClientsQuery(options: ClientsListParams = {}) {
  const [pageIndex, setPageIndex] = useState(0)

  const query = useInfiniteQuery(clientsInfiniteQueryOptions(options))

  const totalPages = query.data?.pages.length ?? 1
  const currentPageData = query.data?.pages[pageIndex] ?? null
  const isOnLastFetchedPage = pageIndex === totalPages - 1
  const hasNextPage = isOnLastFetchedPage ? (query.hasNextPage ?? false) : true

  function goToNextPage() {
    if (isOnLastFetchedPage) {
      void query.fetchNextPage()
    }
    setPageIndex((previous) => previous + 1)
  }

  function goToPrevPage() {
    setPageIndex((previous) => Math.max(0, previous - 1))
  }

  return {
    ...query,
    pageIndex,
    setPageIndex,
    currentPageData,
    totalPages,
    hasNextPage,
    goToNextPage,
    goToPrevPage,
  }
}

export function useClientQuery(id: string, enabled = true) {
  return useQuery({
    queryKey: ['clients', id],
    queryFn: () => getClient(id),
    enabled: !!id && enabled,
  })
}

export function useClientStatsQuery(id: string, enabled = true) {
  return useQuery({
    queryKey: ['clients', id, 'stats'],
    queryFn: () => getClientStats(id),
    enabled: !!id && enabled,
  })
}

export function useCreateClientMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateClient) => {
      const result = await createClient(data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      pushEvent('client_created')
      await queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
    onError: () => pushEvent('form_submit_failed', { form_id: 'client' }),
  })
}

export function useUpdateClientMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateClient }) => {
      const result = await updateClient(id, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['clients'] })
      await queryClient.invalidateQueries({ queryKey: ['clients', variables.id] })
    },
  })
}

export function useDeleteClientMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await deleteClient(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
  })
}

export function useAssignClientTagsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId, tagIds }: { clientId: string; tagIds: string[] }) =>
      assignTagsToClient(clientId, tagIds),
    onSuccess: async (_, { clientId }) => {
      await queryClient.invalidateQueries({ queryKey: ['clients', clientId] })
    },
  })
}

export function useRemoveClientTagMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId, tagId }: { clientId: string; tagId: string }) =>
      removeTagFromClient(clientId, tagId),
    onSuccess: async (_, { clientId }) => {
      await queryClient.invalidateQueries({ queryKey: ['clients', clientId] })
    },
  })
}
